import React from 'react'
import { LocalStorageManager } from '../helper/localStorage'
import { getUrlParamValue } from '../helper/params'

const APP_USER_STORAGE_KEY = 'TRAFFIC_FINE_CONFIRMED_USER_DATA'

const createEmptyUserData = () => ({
  phoneNumberFromSdk: '',
  phoneNumberWithUsernamePrefix: '',
  fullName: '',
  usernamePrefix: ''
})

const cleanPhoneNumber = (value) => String(value || '').replace(/\D/g, '')

function resolveUsernamePrefix(fromParams, fromStored) {
  if (fromParams) return String(fromParams)
  if (fromStored) return String(fromStored)
  return process.env.REACT_APP_USERNAME_PREFIX || 'TAMOVE'
}

function loadStoredUserData() {
  const data = LocalStorageManager.getItem(APP_USER_STORAGE_KEY)
  if (!data || !data.phoneNumberFromSdk) return null
  return data
}

function saveStoredUserData(data) {
  LocalStorageManager.setItem(APP_USER_STORAGE_KEY, {
    phoneNumberFromSdk: data?.phoneNumberFromSdk || '',
    phoneNumberWithUsernamePrefix: data?.phoneNumberWithUsernamePrefix || '',
    fullName: data?.fullName || '',
    usernamePrefix: data?.usernamePrefix || ''
  })
}

function clearStoredUserData() {
  LocalStorageManager.removeItem(APP_USER_STORAGE_KEY)
}

const AppUserContext = React.createContext(null)

export const AppUserContextProvider = ({ children }) => {
  const [userData, setUserData] = React.useState(() => {
    // Sử dụng getUrlParamValue từ params.js thay cho URLSearchParams thủ công
    const phoneFromParams = cleanPhoneNumber(getUrlParamValue('phoneNumber'))
    const prefixFromParams = getUrlParamValue('usernamePrefix')

    const stored = loadStoredUserData()

    // Có phone trên params
    if (phoneFromParams) {
      const resolvedPrefix = resolveUsernamePrefix(prefixFromParams, stored?.usernamePrefix)

      // So sánh cả phoneNumber VÀ usernamePrefix với localStorage
      if (
        stored &&
        stored.phoneNumberFromSdk === phoneFromParams &&
        stored.usernamePrefix === resolvedPrefix
      ) {
        // Cả 2 giống → dùng lại localStorage
        return stored
      }

      // 1 trong 2 khác → tạo mới, lưu lại
      const newData = {
        phoneNumberFromSdk: phoneFromParams,
        phoneNumberWithUsernamePrefix: `${resolvedPrefix}${phoneFromParams}`,
        fullName: '',
        usernamePrefix: resolvedPrefix
      }
      saveStoredUserData(newData)
      return newData
    }

    // Không có phone trên params
    if (stored) {
      // Có data cũ → dùng lại
      // Nếu có prefix mới trên params thì cập nhật prefix
      if (prefixFromParams && String(prefixFromParams) !== stored.usernamePrefix) {
        const updatedData = {
          ...stored,
          usernamePrefix: String(prefixFromParams),
          phoneNumberWithUsernamePrefix: `${prefixFromParams}${stored.phoneNumberFromSdk}`
        }
        saveStoredUserData(updatedData)
        return updatedData
      }
      return stored
    }

    // Không có gì → rỗng
    return createEmptyUserData()
  })

  const setUserInfo = React.useCallback((data) => {
    if (!data) {
      setUserData(createEmptyUserData())
      clearStoredUserData()
      return
    }

    const phone = cleanPhoneNumber(data.phoneNumberFromSdk || data.phoneNumber)
    const prefix = data.usernamePrefix || resolveUsernamePrefix('', '')

    const nextData = {
      phoneNumberFromSdk: phone,
      phoneNumberWithUsernamePrefix: data.phoneNumberWithUsernamePrefix || `${prefix}${phone}`,
      fullName: data.fullName || '',
      usernamePrefix: prefix
    }

    saveStoredUserData(nextData)
    setUserData(nextData)
  }, [])

  const clearUserInfo = React.useCallback(() => {
    clearStoredUserData()
    setUserData(createEmptyUserData())
  }, [])

  const value = React.useMemo(() => ({
    userData,
    setUserInfo,
    clearUserInfo
  }), [userData, setUserInfo, clearUserInfo])

  return <AppUserContext.Provider value={value}>{children}</AppUserContext.Provider>
}

export const useAppUserContext = () => {
  const context = React.useContext(AppUserContext)

  if (!context) {
    throw new Error('useAppUserContext must be used within AppUserContextProvider')
  }

  return context
}
